import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Plural, t, Trans } from '@lingui/macro'
import { Column } from 'react-table'

import TableExtendedOld from 'front-end/src/components/common/table-extended-old/TableExtendedOld'
import {
  ActivityHistoryTableFilters,
  ActivityHistoryTypeEnum,
  ActivityTableSelectionState,
  CPDManualFormValuesWithId,
  CPDSelectedItem
} from '../activityHistoryTypes'
import {
  Box,
  ButtonOutline,
  Checkbox,
  Spacer,
  Flex
} from 'front-end/src/components'
import {
  ActivityHistoryQuery,
  useActivityHistoryQuery,
  useMarkItemsAsCpdMutation
} from '../ActivityHistory.codegen'
import {
  ActivityHistoryOrderColumn,
  SqlOrderDirectionEnum
} from '../../../graphql/private/PrivateSchemaTypes'
import ActivityHistoryTableName from './ActivityHistoryTableName'
import { getPagesCount } from '../../analytics/utils'
import { DEFAULT_ACTIVITY_LIMIT } from '../activityHistoryConstants'
import ActivityHistoryRowStatus from './ActivityHistoryRowStatus'
import { css } from '@emotion/css'
import ActivityHistoryDownloadCSV from './ActivityHistoryDownloadCSV'
import {
  checkIsCPDEvidence,
  checkIsPageSelected,
  formatActivityScore,
  formatDuration,
  formatRowToCpdSelectedItem,
  getActivityFormattedDate,
  getActivityRowsSelection
} from '../activityHistoryUtils'
import IconPlusInCircle from 'front-end/src/components/common/icon/IconPlusInCircle'
import ActivityHistoryCPDManualModal from 'front-end/src/pages/activity-history/components/ActivityHistoryCPDManualModal'
import ActivityHistoryRowMenu from 'front-end/src/pages/activity-history/components/ActivityHistoryRowMenu'
import ActivityHistoryCPDRemoveConfirmModal from 'front-end/src/pages/activity-history/components/ActivityHistoryCPDRemoveConfirmModal'
import ActivityHistoryTableBulkAction from 'front-end/src/pages/activity-history/components/ActivityHistoryTableBulkAction'
import IconBookOpen from 'front-end/src/components/common/icon/IconBookOpen'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import { useCPDFeatureFlag } from 'front-end/src/app/hooks/useFeatureFlags'
import { brandColors } from 'shared/basicPalette'

interface ActivityHistoryHeaderProps {
  dateFilters: {
    from: string
    to: string
  }
  tableFilters: ActivityHistoryTableFilters
  updateTableFilters: (newFilters: ActivityHistoryTableFilters) => void
  isCpdOnlyFilter: boolean
  isCpdEnabled: boolean
  refetchStats: () => Promise<any>
}

const ActivityHistoryTable = ({
  dateFilters,
  tableFilters,
  updateTableFilters,
  isCpdOnlyFilter,
  isCpdEnabled,
  refetchStats
}: ActivityHistoryHeaderProps) => {
  const { theme } = useLooopTheme()

  const filters = { ...tableFilters, cpdOnly: isCpdOnlyFilter, ...dateFilters }

  const { cpdAlias } = useCPDFeatureFlag()

  const { data, loading, refetch } = useActivityHistoryQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filters
    },
    notifyOnNetworkStatusChange: true
  })
  const [currentData, setCurrentData] = useState<
    ActivityHistoryQuery | undefined
  >(data)
  const activityHistoryData = currentData?.me.activityHistory

  const totalPages = getPagesCount(
    activityHistoryData?.count,
    DEFAULT_ACTIVITY_LIMIT
  )

  // need to update both because we can change number of CPD hours
  const refetchTableAndStats = useCallback(async () => {
    refetch()
    refetchStats()
  }, [])

  const [markItemsAsCpd, { loading: isCpdMarkLoading }] =
    useMarkItemsAsCpdMutation({
      onCompleted: refetchTableAndStats
    })

  const [cpdModalState, setCpdModalState] = useState<{
    isOpened: boolean
    cpdEditData: null | CPDManualFormValuesWithId
  }>({
    isOpened: false,
    cpdEditData: null
  })

  const openCPDEditModal = useCallback(
    (formData: CPDManualFormValuesWithId) => {
      setCpdModalState({ isOpened: true, cpdEditData: formData })
    },
    []
  )
  const openCPDModal = useCallback(() => {
    setCpdModalState((prevState) => ({ ...prevState, isOpened: true }))
  }, [])
  const closeCPDModal = useCallback(() => {
    setCpdModalState((prevState) => ({ ...prevState, isOpened: false }))
  }, [])
  const clearEditData = useCallback(() => {
    setCpdModalState((prevState) => ({ ...prevState, cpdEditData: null }))
  }, [])

  const [deleteCpdId, setDeleteCpdId] = useState<number | null>(null)
  const [selectedRowsData, selectRows] = useState<ActivityTableSelectionState>({
    items: [],
    ids: []
  })

  const handleSelect = (rowData: CPDSelectedItem, checked: boolean) => {
    if (checked) {
      selectRows((prevState) => ({
        items: [...prevState.items, rowData],
        ids: [...prevState.ids, rowData.itemId]
      }))
    } else {
      selectRows((prevState) => ({
        items: prevState.items.filter((item) => item.itemId !== rowData.itemId),
        ids: prevState.ids.filter((id) => id !== rowData.itemId)
      }))
    }
  }

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (!currentData || !currentData?.me?.activityHistory?.items) return

    const newSelection = getActivityRowsSelection(
      currentData?.me?.activityHistory?.items,
      event.currentTarget.checked,
      selectedRowsData
    )

    selectRows(newSelection)
  }

  const isPageSelected = useMemo(
    () =>
      checkIsPageSelected(
        selectedRowsData.ids,
        currentData?.me?.activityHistory?.items
      ),
    [selectedRowsData.ids, currentData]
  )

  const tableColumns: Column<any>[] = [
    {
      show: isCpdEnabled,
      Header: () => {
        if (!currentData?.me?.activityHistory?.items) return null

        return (
          <Checkbox
            aria-label={t`Select all`}
            isChecked={isPageSelected}
            onChange={handleSelectAll}
          />
        )
      },
      headerStyle: {
        paddingLeft: 8,
        paddingRight: 8
      },
      accessor: 'checked',
      id: 'checked',
      width: 40,
      sortable: false,
      Cell: (row) => {
        const rowData = row.original

        const cpdSelectedItem = formatRowToCpdSelectedItem(rowData)

        if (!cpdSelectedItem) {
          return null
        }

        return (
          <Checkbox
            aria-label={t`Selected`}
            isChecked={selectedRowsData.ids.includes(row.original.id)}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              handleSelect(cpdSelectedItem, event.currentTarget.checked)
            }}
          />
        )
      }
    },
    {
      Header: t`Activity name`,
      id: ActivityHistoryOrderColumn.activityName,
      accessor: 'activityName',
      Cell: (row) => {
        return <ActivityHistoryTableName {...row.original} />
      },
      style: {
        whiteSpace: 'normal'
      }
    },
    {
      Header: t`Status`,
      accessor: 'status',
      sortable: false,
      minWidth: 130,
      maxWidth: 180,
      Cell: (row) => {
        return <ActivityHistoryRowStatus {...row.original} />
      }
    },
    {
      Header: t`Started`,
      width: 120,
      accessor: 'startedAt',
      Cell: (row) => {
        const rowData = row.original

        return getActivityFormattedDate(
          rowData?.started_at ||
            rowData?.event?.start_time ||
            rowData?.date_started
        )
      }
    },
    {
      Header: t`Completed`,
      accessor: 'completedAt',
      width: 120,
      Cell: (row) => {
        const rowData = row.original
        return getActivityFormattedDate(
          rowData?.completed_at ||
            rowData?.event?.end_time ||
            rowData?.date_completed
        )
      }
    },
    {
      Header: t`Score`,
      width: 80,
      sortable: false,
      Cell: (row) => {
        return formatActivityScore(row.original)
      }
    },
    {
      Header: t`Version`,
      sortable: false,
      width: 80,
      Cell: (row) => {
        if (
          row.original.__typename ===
          ActivityHistoryTypeEnum.TopicEnrolmentModelAsStudent
        ) {
          return row.original?.topicVersion?.version
        }

        return null
      }
    },
    {
      Header: t`Duration`,
      sortable: false,
      width: 120,
      Cell: (row) => {
        return formatDuration(row.original)
      }
    },
    {
      show: isCpdEnabled,
      Header: cpdAlias,
      sortable: false,
      width: 50,
      Cell: (row) => {
        const rowData = row.original

        const isShowCpdIcon = checkIsCPDEvidence(rowData) || rowData.isCpdEntry

        return isShowCpdIcon ? (
          <Flex
            border={`1px solid ${theme.colors.violet[600]}`}
            p="0 8px"
            borderRadius="100px"
            h="22px"
            alignItems="center"
          >
            <IconBookOpen color={theme.colors.violet[600]} fontSize="12px" />
          </Flex>
        ) : null
      }
    },
    {
      show: isCpdEnabled,
      Header: '',
      sortable: false,
      width: 50,
      Cell: (row) => {
        return (
          <ActivityHistoryRowMenu
            {...row.original}
            openEditCPDModal={openCPDEditModal}
            opedDeleteCPDModal={setDeleteCpdId}
            markItemsAsCpd={markItemsAsCpd}
          />
        )
      }
    }
  ]

  useEffect(() => {
    selectRows({
      items: [],
      ids: []
    })
  }, [isCpdOnlyFilter, filters.from, filters.to, filters.search])

  useEffect(() => {
    if (!loading) {
      setCurrentData(data)
    }
  }, [data, loading])

  return (
    <Box mt="24px">
      <TableExtendedOld
        loading={loading || isCpdMarkLoading}
        manual
        data={activityHistoryData?.items}
        pages={totalPages}
        defaultPageSize={filters.limit}
        page={filters.page - 1}
        columns={tableColumns}
        defaultSorted={[
          {
            id: filters.orderColumn,
            desc: filters.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          const newOrder = newSorted[0].id
          const descendingOrder = newSorted[0] ? newSorted[0].desc : false

          updateTableFilters({
            ...tableFilters,
            orderColumn: newOrder as ActivityHistoryOrderColumn,
            orderDirection: descendingOrder
              ? SqlOrderDirectionEnum.DESC
              : SqlOrderDirectionEnum.ASC
          })
        }}
        onPageChange={(newPage) => {
          updateTableFilters({
            ...tableFilters,
            page: newPage + 1
          })
        }}
        onSearchChange={(searchVal) => {
          updateTableFilters({
            ...tableFilters,
            page: 1,
            search: searchVal
          })
        }}
        className={css`
          .TableExtended__filter {
            width: 100%;
          }

          .rt-tr-group:hover {
            .ActivityHistory__menu__button,
            a {
              opacity: 1;
            }
          }
        `}
        resizable={false}
        minRows={5}
        showPageSizeOptions={false}
        showSearchFilter={
          (activityHistoryData?.items &&
            activityHistoryData.items.length > 0) ||
          !!filters.search
        }
        defaultSearch={filters.search}
        filterContentBefore={
          isCpdEnabled ? (
            <ActivityHistoryTableBulkAction
              selectedItems={selectedRowsData.items}
              markItemsAsCpd={markItemsAsCpd}
              isLoading={isCpdMarkLoading || loading}
            />
          ) : undefined
        }
        extendFilter={
          <>
            <Spacer />
            <Box>
              {!loading && isCpdEnabled && (
                <ButtonOutline
                  leftIcon={<IconPlusInCircle />}
                  onClick={openCPDModal}
                >
                  <Trans>Add external activity</Trans>
                </ButtonOutline>
              )}
              {activityHistoryData?.items &&
                activityHistoryData.items.length > 0 && (
                  <ActivityHistoryDownloadCSV
                    filters={{ filters }}
                    count={activityHistoryData.count}
                    isCpdEnabled={isCpdEnabled}
                  />
                )}
            </Box>
          </>
        }
        paginationContentBefore={
          activityHistoryData && Number.isFinite(activityHistoryData?.count) ? (
            <Box color={brandColors.grey.mid}>
              <Plural
                value={activityHistoryData.count}
                _0={null}
                one={`# activity`}
                other={`# activities`}
              />
            </Box>
          ) : undefined
        }
      />
      <ActivityHistoryCPDManualModal
        isOpen={cpdModalState.isOpened}
        onClose={closeCPDModal}
        editData={cpdModalState.cpdEditData}
        userId={currentData?.me.id}
        clearEditData={clearEditData}
        refetch={refetchTableAndStats}
      />
      <ActivityHistoryCPDRemoveConfirmModal
        deleteId={deleteCpdId}
        handleClose={() => setDeleteCpdId(null)}
        refetchTable={refetchTableAndStats}
      />
    </Box>
  )
}

export default ActivityHistoryTable
